/**
 * Expanded Result Groups
 *
 * The global-search composition of the Cmd-K palette, rendered only in
 * navigate mode. Owns the single shared `<Command.List>` so keyboard
 * navigation and the controlled highlight span every row in a fixed order when
 * filters are hidden:
 *
 *   Top result → Content (server search, with a reserved skeleton while
 *   fetching) → Go to → Settings → pinned Ask Bird.
 *
 * It also OWNS `useCommandRegistry` (and therefore the auth/query-dependent
 * `useCanUseFeature` graph check). Keeping that hook here — not in the parent —
 * means select and parent-picker palettes never pull in those providers: it
 * only runs when this component is actually mounted (global navigate mode).
 * Content reuses the existing {@link SearchResultsList} with `wrapInList=false`
 * so grouping + infinite scroll are shared, not duplicated.
 */

import { useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { Command } from 'cmdk';
import { StaticResultGroup } from './StaticResultGroup';
import { ChatResultItem } from './ChatResultItem';
import { RecentChatsGroup } from './RecentChatsGroup';
import { ContentSkeleton } from './ContentSkeleton';
import { SearchResultsList, type ResultSection } from './SearchResultsList';
import type { PaletteSections } from './paletteSections';
import { useCommandRegistry } from '../../../hooks/data/useCommandRegistry';
import { useHelpSearch } from '../../../hooks/data/useHelpSearch';
import { useChatSearchQuery } from '../../../hooks/data/useChatSearchQuery';
import { useRecentChats } from '../../../hooks/data/useRecentChats';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import type { SearchResult } from '../../../services/search.service';
import type { ChatSearchResult } from '../../../services/agentChat.service';
import { openBirdChat } from '../../../services/events/birdChatEvents';
import { scopeShowsContent, showsEveryZone, type SearchScope } from './SearchScopeFilter';
import type { CommandEntry, CommandGroup } from '../../../constants/commandRegistry';

/**
 * Whether a command group is on screen. A visible scope row narrows to one
 * command kind; hiding that row deliberately returns to the full palette, so
 * no command group depends on controls that are no longer on screen.
 */
function showsGroup(scope: SearchScope, group: CommandGroup, showAllResultZones: boolean): boolean {
  if (showsEveryZone(scope, showAllResultZones)) return true;
  if (group === 'goto') return scope === 'navigation';
  if (group === 'settings') return scope === 'settings';
  if (group === 'help') return scope === 'help';
  return false;
}

/**
 * Which set of Help rows to render. See the call site for why the live index
 * and the curated registry are alternatives rather than a merge.
 */
function resolveHelpRows({
  isBrowsing,
  curatedBrowse,
  live,
  curatedScored,
  degraded,
  isFetching,
}: {
  isBrowsing: boolean;
  curatedBrowse: readonly CommandEntry[];
  live: readonly CommandEntry[];
  curatedScored: readonly CommandEntry[];
  degraded: boolean;
  isFetching: boolean;
}): readonly CommandEntry[] {
  if (isBrowsing) return curatedBrowse;
  if (live.length > 0) return live;
  // Curated is the fallback for "couldn't ask" — an outage, a PHI block, or a
  // request still in flight (so the group never flashes empty mid-keystroke).
  if (degraded || isFetching) return curatedScored;
  // Settled, healthy, and the index genuinely matched nothing. Show an empty
  // group rather than curated rows the index has already judged irrelevant —
  // otherwise "no such article" is indistinguishable from "search is broken".
  return [];
}

/** The highlight-anchor targets the parent needs for the selection contract. */
export interface AnchorHint {
  readonly topId: string | null;
  readonly firstStaticId: string | null;
  /**
   * Every static row currently on screen, in render order, WITH its group.
   *
   * Needed because Help rows now CHANGE IDENTITY within one query: curated
   * `help-*` ids paint first, then the live index replaces them with rows keyed
   * by article URL. `StaticResultGroup` renders `forceMount`, so cmdk never
   * registers these items and its own "selected item unmounted" recovery never
   * runs — without this list a frozen highlight can point at a row that no
   * longer exists, which is a silently dead Enter key.
   *
   * The GROUP travels with the id so a re-point can stay inside the group the
   * user was reading. Clamping across the flattened list instead walks off the
   * end of an emptying Help group and into Settings — the wrong-Enter this
   * whole mechanism exists to prevent, just relocated.
   */
  readonly staticRows: ReadonlyArray<{ readonly id: string; readonly group: CommandGroup }>;
}

interface ExpandedResultGroupsProps {
  query: string;
  /** A change (scope, Types or query) starts the list at the top. */
  scrollResetKey: string;
  mode: 'navigate' | 'select';
  /** Which zones to render. Filters by KIND of answer, not by entity type. */
  scope: SearchScope;
  /** True when the scope controls are hidden, so every result zone remains reachable. */
  showAllResultZones: boolean;
  /** Closes + resets the palette (used by static selects and the Ask Bird row). */
  onClose: () => void;
  /** Reports the current Top / first-static ids up for the parent's anchor logic. */
  onAnchorChange: (hint: AnchorHint) => void;
  // Content zone state for global search.
  isShowingRecents: boolean;
  hasResults: boolean;
  isFetching: boolean;
  filteredResults: SearchResult[];
  sections: PaletteSections | null;
  onSelectContent: (result: SearchResult, section: ResultSection) => void;
  onShowAll: () => void;
  /** `preview` on the All tab, `full` on the Workspace tab. */
  layout: 'preview' | 'full';
  recentsHeading: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /** The content request failed with nothing to show; see SearchResultsList. */
  contentFailure?: Error | null;
  onRetryContent?: () => void;
}

export function ExpandedResultGroups({
  query,
  scrollResetKey,
  mode,
  scope,
  showAllResultZones,
  onClose,
  onAnchorChange,
  isShowingRecents,
  hasResults,
  isFetching,
  filteredResults,
  sections,
  onSelectContent,
  onShowAll,
  layout,
  recentsHeading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  contentFailure,
  onRetryContent,
}: ExpandedResultGroupsProps) {
  const { top, goto, settings, help, browse, perform } = useCommandRegistry(query);

  // Live help-centre search. Only fires where the Help group is actually on
  // screen, so the 'navigation' / 'settings' scopes cost no network.
  const {
    entries: liveHelp,
    degraded: helpDegraded,
    isFetching: helpFetching,
  } = useHelpSearch(query, { enabled: showsGroup(scope, 'help', showAllResultZones) });

  // An empty visible command scope is a BROWSE, not a search: clicking
  // "Settings" should list the settings. The hidden-row full view keeps the
  // normal scored composition rather than opening a wall of commands.
  const isBrowsing = !query.trim() && !showAllResultZones;
  const gotoRows = isBrowsing ? browse.goto : goto;
  const settingsRows = isBrowsing ? browse.settings : settings;

  /**
   * Help rows come from the live docs index when it has answers, and from the
   * curated `HELP_LINKS` registry otherwise.
   *
   * They are NOT merged. The registry is a dozen labels matched on the LABEL;
   * the index is the actual article corpus and a strict superset of those
   * pages, so interleaving would show the same page twice under two titles.
   * The curated list is the fallback while the index is still building (so
   * the group never flashes empty between keystrokes) or if it could not be
   * built at all.
   *
   * Browsing with an empty query stays curated — there is nothing to search.
   *
   * Every arm is an already-memoised array from its own hook, so this
   * allocates nothing and `helpRows` stays referentially stable for the anchor
   * effect below.
   */
  const helpRows = resolveHelpRows({
    isBrowsing,
    curatedBrowse: browse.help,
    live: liveHelp,
    curatedScored: help,
    degraded: helpDegraded,
    isFetching: helpFetching,
  });

  /**
   * Which corpus the user is actually looking at. Referential where it can be
   * (`resolveHelpRows` returns the `liveHelp` array itself on the live arm),
   * and `degraded` is threaded through so an OUTAGE is distinguishable from an
   * ordinary curated fallback. Without this the two corpora are
   * indistinguishable in PostHog and the feature's thesis — that the index
   * finds articles the curated list can't — is unfalsifiable.
   */
  const helpSource: 'live' | 'curated' | 'degraded' = (() => {
    if (helpRows === liveHelp) return 'live';
    return helpDegraded ? 'degraded' : 'curated';
  })();

  // Report anchor targets up whenever the registry results change. Content
  // (priority 2) lives in the parent, so only the static ids are lifted.
  useEffect(() => {
    // Anchors must never name a row the current scope doesn't render, or the
    // controlled highlight lands on nothing and the arrow keys go dead.
    const rendered = [
      ...(showsGroup(scope, 'goto', showAllResultZones) ? gotoRows : []),
      ...(showsGroup(scope, 'settings', showAllResultZones) ? settingsRows : []),
      ...(showsGroup(scope, 'help', showAllResultZones) ? helpRows : []),
    ];
    onAnchorChange({
      topId: top && showsGroup(scope, top.group, showAllResultZones) ? top.id : null,
      firstStaticId: rendered[0]?.id ?? null,
      staticRows: rendered.map((entry) => ({ id: entry.id, group: entry.group })),
    });
  }, [top, gotoRows, settingsRows, helpRows, scope, showAllResultZones, onAnchorChange]);

  // Select a static entry: analytics (IDs/counts only — never the query text),
  // close, then navigate.
  const handleStaticSelect = useCallback(
    (entry: CommandEntry) => {
      safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_RESULT_CLICKED, {
        result_category: entry.group,
        // Help rows only: an enum of two literals, never the article id (which
        // is its URL). Distinguishes a live-index hit from a curated fallback,
        // which is also the only signal that the index is degrading in prod.
        ...(entry.group === 'help' ? { help_source: helpSource } : {}),
        query_length: query.length,
        mode,
      } as Record<string, unknown>);
      onClose();
      perform(entry);
    },
    [query, mode, onClose, perform, helpSource],
  );

  const showWorkspace = scopeShowsContent(scope, showAllResultZones);
  const showsChats = showsEveryZone(scope, showAllResultZones) || scope === 'chats';

  // Only fetch when the zone is actually on screen — the palette mounts on
  // every page, and an always-on query would put a request behind every
  // keystroke for users who never open the Chats scope.
  const { results: chatResults, degraded: chatSearchDegraded } = useChatSearchQuery(query, {
    enabled: showsChats,
  });

  // The Chats scope before anything is typed: the caller's recent
  // conversations, so the tab is never an empty sheet.
  const hasQuery = query.trim().length > 0;
  // Only on the Chats tab itself, not when the filter row is hidden and every
  // zone shows.
  const showsRecentChats = scope === 'chats' && !showAllResultZones && !hasQuery;
  // Chat search rows only with something typed, on any tab: the search keeps
  // the last query's matches as placeholder data after the query is cleared.
  const showsChatSearch = showsChats && hasQuery;
  const recentChats = useRecentChats({ enabled: showsRecentChats });

  const handleRecentChatSelect = useCallback(
    (result: ChatSearchResult) => {
      safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_RESULT_CLICKED, {
        result_category: 'chat',
        result_section: 'recent',
        query_length: 0,
        mode,
      } as Record<string, unknown>);
      onClose();
      openBirdChat({ source: 'palette_recent_chats', sessionId: result.session_id });
    },
    [mode, onClose],
  );

  const handleChatSelect = useCallback(
    (result: ChatSearchResult) => {
      safeAnalytics.track(ANALYTICS_EVENTS.SEARCH_RESULT_CLICKED, {
        result_category: 'chat',
        query_length: query.length,
        mode,
      } as Record<string, unknown>);
      onClose();
      // Carries the matched message so the panel can scroll to it rather than
      // dropping the user at the top of a long conversation.
      openBirdChat({
        source: 'chat_search',
        sessionId: result.session_id,
        messageId: result.message_id,
      });
    },
    [query, mode, onClose],
  );

  // New results (scope, Types or query) start at the top of the list, or the
  // best match can sit above the fold. Layout effect, so the old offset never
  // paints.
  const listRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  return (
    <Command.List ref={listRef}>
      {/* Top result is a COMMAND, so it only shows where its own group does —
          otherwise "Settings" would be the one filter still showing a Go-to. */}
      {top && showsGroup(scope, top.group, showAllResultZones) && (
        <StaticResultGroup heading="Top result" entries={[top]} onSelect={handleStaticSelect} />
      )}

      {/* Content zone: reserved skeleton while fetching, then grouped results.
          0 results collapses silently (hideEmptyStates); a FAILED request
          does not, it shows an error with a retry. */}
      {showWorkspace && (
        <SearchResultsList
          wrapInList={false}
          // Most of the six-row preview, so the sections below barely move when
          // the rows arrive.
          loadingContent={<ContentSkeleton rows={4} />}
          hideEmptyStates
          isShowingRecents={isShowingRecents}
          hasResults={hasResults}
          isFetching={isFetching}
          showEmpty={false}
          filteredResults={filteredResults}
          sections={sections}
          onSelect={onSelectContent}
          query={query}
          onShowAll={onShowAll}
          layout={layout}
          recentsHeading={recentsHeading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={onLoadMore}
          failure={contentFailure}
          onRetry={onRetryContent}
        />
      )}

      {/* Chats: the user's own conversations. Its own zone in the fixed order,
          NOT merged into the content zone — the palette deliberately ranks
          within zones and never across them, so there is no cross-source score
          to normalise here either. */}
      {/* A search that could not run says so. Rendering it as an empty zone
          would tell the user their own history lacks a word they used. */}
      {showsChatSearch && chatSearchDegraded && chatResults.length === 0 && (
        <Command.Group heading="Chats">
          <Box px={3} py={3}>
            <Text fontSize="sm" color="text.muted">
              Search took too long. Try a narrower term.
            </Text>
          </Box>
        </Command.Group>
      )}

      {showsRecentChats && <RecentChatsGroup {...recentChats} onSelect={handleRecentChatSelect} />}

      {showsChatSearch && chatResults.length > 0 && (
        <Command.Group heading="Chats">
          {chatResults.map((result) => (
            <ChatResultItem
              key={`${result.session_id}:${result.message_id}`}
              result={result}
              onSelect={handleChatSelect}
            />
          ))}
        </Command.Group>
      )}

      {showsGroup(scope, 'goto', showAllResultZones) && (
        <StaticResultGroup heading="Go to" entries={gotoRows} onSelect={handleStaticSelect} />
      )}
      {showsGroup(scope, 'settings', showAllResultZones) && (
        <StaticResultGroup
          heading="Settings"
          entries={settingsRows}
          onSelect={handleStaticSelect}
        />
      )}
      {showsGroup(scope, 'help', showAllResultZones) && (
        <StaticResultGroup heading="Help center" entries={helpRows} onSelect={handleStaticSelect} />
      )}
    </Command.List>
  );
}
